import React, { useState, useRef, useEffect } from 'react'
import classNames from 'classnames'
import { isMobile } from 'react-device-detect'

interface DropdownOption {
  value: string
  label: string
  disabled?: boolean
}

interface DropdownSelectProps {
  options: DropdownOption[]
  values?: string[]
  onChange?: (values: string[]) => void
  placeholder?: string
  className?: string
}

export const DropdownSelect: React.FC<DropdownSelectProps> = ({
  options,
  values = [],
  onChange,
  placeholder = '선택',
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // 외부 클릭 감지하여 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleSelect = (optionValue: string, disabled?: boolean) => {
    if (disabled) return
    
    const newValues = values.includes(optionValue)
      ? values.filter((v) => v !== optionValue)
      : [...values, optionValue]
    onChange?.(newValues)
  }

  const getDisplayText = () => {
    if (values.length === 0) return placeholder
    const selectedLabels = values.map((value) => {
      const option = options.find((opt) => opt.value === value)
      return option?.label || value
    })
    return selectedLabels.join(', ')
  }

  return (
    <div className={classNames('relative', className)} ref={dropdownRef}>
      <button
        className={classNames(
          'px-4 py-1 border-[1px] border-[#D4D9DF] rounded-[6px] font-[500] text-[14px] text-left bg-white',
          {
            'min-w-[140px] h-auto': !isMobile,
            'w-[140px] h-auto': isMobile,
          }
        )}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
      >
        {getDisplayText()}
      </button>

      <img
        src="assets/img/chevron-down.png"
        className={classNames(
          'absolute right-1 w-[16px] h-[16px] bottom-2',
          {}
        )}
        alt=""
        onClick={() => setIsOpen(!isOpen)}
      />

      {isOpen && (
        <div className="absolute bg-white z-50 shadow-main flex flex-col px-3 py-5 gap-2 justify-center rounded-[10px] mt-1 min-w-[140px] w-auto">
          {options.map((option, index) => (
            <div
              className={classNames(
                "flex items-center gap-2 whitespace-nowrap",
                {
                  "opacity-50 cursor-not-allowed": option.disabled,
                  "cursor-pointer": !option.disabled,
                }
              )}
              key={`option-${index}`}
            >
              <input
                id={`${option.value}-${index}`}
                type="checkbox"
                className="w-[20px] h-[20px] flex-shrink-0"
                value={option.value}
                checked={values.includes(option.value)}
                disabled={option.disabled}
                onChange={() => handleSelect(option.value, option.disabled)}
              />
              <label 
                htmlFor={`${option.value}-${index}`} 
                className={classNames(
                  "text-sm",
                  {
                    "text-gray-400": option.disabled,
                    "text-black": !option.disabled,
                  }
                )}
              >
                {option.label}
              </label>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
